import {
  PerformanceTableColumn,
  PerformanceTableFragment,
  PerformanceTableRow,
} from '../../../generated/graphql'
import { inverseLerp, lerp } from '../../../util/lerp'

function findValueFromColumns(
  columns: PerformanceTableColumn[],
  condenserTemperature: number,
): number {
  if (condenserTemperature > columns[columns.length - 1].condenserTemperature) {
    // Requested temperatures are outside of acceptable range.
    return NaN
  }

  const temperature = Math.max(
    condenserTemperature,
    columns[0].condenserTemperature,
  )

  for (let i = 0; i < columns.length - 1; i++) {
    const currentColumn = columns[i]
    const nextColumn = columns[i + 1]

    if (
      currentColumn.condenserTemperature <= temperature &&
      temperature <= nextColumn.condenserTemperature
    ) {
      const t = inverseLerp(
        currentColumn.condenserTemperature,
        nextColumn.condenserTemperature,
        temperature,
      )

      if (
        t > 0 &&
        t < 1 &&
        (currentColumn.value === 0 || nextColumn.value === 0)
      ) {
        // The combination of temperatures is not within the operational range of the heat pump.
        return NaN
      }

      return lerp(currentColumn.value, nextColumn.value, t)
    }
  }

  throw new Error(
    'Should never end up here, is the table sorted from the lowest to the highest temperature?',
  )
}

/**
 * This is practically bilinear interpolation with some special cases (the NaN returns).
 */
function findValueFromRows(
  rows: PerformanceTableRow[],
  evaporatorTemperature: number,
  condenserTemperature: number,
): number {
  if (evaporatorTemperature < rows[0].evaporatorTemperature) {
    // Requested temperatures are outside of acceptable range.
    return NaN
  }

  const temperature = Math.min(
    evaporatorTemperature,
    rows[rows.length - 1].evaporatorTemperature,
  )

  for (let i = 0; i < rows.length - 1; i++) {
    const currentRow = rows[i]
    const nextRow = rows[i + 1]

    if (
      currentRow.evaporatorTemperature <= temperature &&
      temperature <= nextRow.evaporatorTemperature
    ) {
      const t = inverseLerp(
        currentRow.evaporatorTemperature,
        nextRow.evaporatorTemperature,
        temperature,
      )

      const currentRowValue = findValueFromColumns(
        currentRow.columns,
        condenserTemperature,
      )
      const nextRowValue = findValueFromColumns(
        nextRow.columns,
        condenserTemperature,
      )

      if (
        t > 0 &&
        t < 1 &&
        (Number.isNaN(currentRowValue) || Number.isNaN(nextRowValue))
      ) {
        // The combination of temperatures is not within the operational range of the heat pump.
        return NaN
      }

      return lerp(currentRowValue, nextRowValue, t)
    }
  }

  throw new Error(
    'Should never end up here, is the table sorted from the lowest to the highest temperature?',
  )
}

export type TablePerformance = {
  cop: number
  powerCoefficient: number
}

export function getPerformanceFromTable(
  performanceTable: PerformanceTableFragment,
  evaporatorTemperature: number,
  condenserTemperature: number,
  copModifier: number,
): TablePerformance {
  if (
    Number.isNaN(evaporatorTemperature) ||
    Number.isNaN(condenserTemperature)
  ) {
    return { cop: NaN, powerCoefficient: NaN }
  }

  const cop = findValueFromRows(
    performanceTable.copTable,
    evaporatorTemperature,
    condenserTemperature,
  )

  return {
    cop: Math.max(1.1, cop * copModifier),
    powerCoefficient: findValueFromRows(
      performanceTable.powerCoefficientTable,
      evaporatorTemperature,
      condenserTemperature,
    ),
  }
}
